import type { BillingCycle, PlanWithFrequency } from '@features/onboarding/hooks/use-plans';
import type { PlanFlowOption } from './PlanFlowModal';

/**
 * Turn the catalogue into what the flow selects from.
 *
 * The catalogue states prices in reais and the billing API speaks cents, so
 * the conversion lives here, once, next to its test. Getting it wrong does
 * not misalign a layout — it charges the wrong amount.
 *
 * @param plans - The catalogue as onboarding loads it.
 * @returns One option per plan, able to answer for either cycle.
 */
export function toFlowOptions(plans: PlanWithFrequency[]): PlanFlowOption[] {
  return plans.map((plan) => {
    const priceFor = (cycle: BillingCycle): number => {
      const price = cycle === 'yearly'
        ? (plan.yearlyPrice ?? plan.price)
        : (plan.monthlyPrice ?? plan.price);
      return Math.round(price * 100);
    };

    return {
      key: plan.key,
      title: plan.title,
      priceCents: priceFor('monthly'),
      // A yearly price beside a monthly one compares nothing. Twelfths do.
      monthlyEquivalentCents: plan.yearlyPrice
        ? Math.round((plan.yearlyPrice / 12) * 100)
        : priceFor('monthly'),
      limits: plan.limits?.seat ?? plan.limits?.agency,
      features: Object.fromEntries(
        (plan.features ?? []).map((feature) => [feature.key, feature.tier !== 'none']),
      ),
      planIdFor: (cycle: BillingCycle) =>
        (cycle === 'yearly' ? plan.yearlyId : plan.monthlyId) ?? plan.value,
      priceFor,
    };
  });
}
